export const PERMISSION_RESOURCE_CODES = {
  permission: 'permission',
  selfProfile: 'employee-center-self-profile',
  selfCertificateRequests: 'employee-center-self-certificate-requests',
  selfLifecycle: 'employee-center-self-lifecycle',
  selfResignationHandover: 'employee-center-self-resignation-handover',
  attendanceMyOverview: 'attendance-center-my-overview',
  attendanceMyRecords: 'attendance-center-my-records',
  attendanceMyRequests: 'attendance-center-my-requests',
  myRequests: 'attendance-center-my-requests',
  attendanceMyLeaveBalances: 'attendance-center-my-leave-balances',
  attendanceMyMonthly: 'attendance-center-my-monthly',
  attendanceApproval: 'attendance-center-approval',
  approval: 'attendance-center-approval',
  payrollMy: 'payroll-center-my-slips',
  socialInsuranceMy: 'social-insurance-center-my',
  performanceMy: 'performance-center-my',
  records: 'attendance-center-records',
  monthlySummaries: 'attendance-center-monthly-summaries',
  leaveRequests: 'attendance-center-leave-requests',
  overtimeRequests: 'attendance-center-overtime-requests',
  punchRequests: 'attendance-center-punch-requests',
  leaveBalances: 'attendance-center-leave-balances',
  groups: 'attendance-center-groups',
  rules: 'attendance-center-rules',
  shifts: 'attendance-center-shifts',
  schedules: 'attendance-center-schedules',
  holidayPlans: 'attendance-center-holiday-plans',
  leaveGrantPolicies: 'attendance-center-leave-grant-policies',
  settings: 'attendance-center-settings',
  workflowSettings: 'attendance-center-workflow-settings',
  operationAudits: 'attendance-center-operation-audits',
  dailyResults: 'attendance-center-daily-results',
  teamOverview: 'attendance-center-team-overview',
  teamSchedules: 'attendance-center-team-schedules',
  teamExceptions: 'attendance-center-team-exceptions',
  teamMonthly: 'attendance-center-team-monthly',
  workbench: 'attendance-center-workbench',
} as const;

export const PERMISSION_ACTION_CODES = {
  permission: {},
  selfProfile: {
    view: 'view',
    query: 'query',
    editProfile: 'edit-profile',
    uploadAttachment: 'upload-attachment',
    downloadAttachment: 'download-attachment',
  },
  selfCertificateRequests: {
    view: 'view',
    query: 'query',
    createRequest: 'create-request',
    editRequest: 'edit-request',
    uploadAttachment: 'upload-attachment',
    submitRequest: 'submit-request',
    cancelRequest: 'cancel-request',
    withdrawRequest: 'withdraw-request',
    downloadResult: 'download-result',
  },
  selfLifecycle: { view: 'view', query: 'query', confirm: 'confirm' },
  selfResignationHandover: {
    view: 'view',
    query: 'query',
    uploadAttachment: 'upload-attachment',
    saveDraft: 'save-draft',
    submitHandover: 'submit-handover',
  },
  attendanceMyOverview: {
    view: 'view',
    query: 'query',
    openDrilldown: 'open-drilldown',
  },
  attendanceMyRecords: { view: 'view', query: 'query' },
  attendanceMyRequests: {
    view: 'view',
    query: 'query',
    createLeave: 'create-leave',
    createOvertime: 'create-overtime',
    createPunch: 'create-punch',
    submitRequest: 'submit-request',
    cancelRequest: 'cancel-request',
  },
  attendanceMyLeaveBalances: { view: 'view', query: 'query' },
  attendanceMyMonthly: {
    view: 'view',
    query: 'query',
    downloadMonthly: 'download-monthly',
  },
  attendanceApproval: {
    view: 'view',
    query: 'query',
    openSource: 'open-source',
  },
  payrollMy: {
    view: 'view',
    query: 'query',
    viewSlip: 'view-slip',
    confirmSlip: 'confirm-slip',
  },
  socialInsuranceMy: { view: 'view', query: 'query' },
  performanceMy: {
    view: 'view',
    query: 'query',
    updateGoalProgress: 'update-goal-progress',
    saveReview: 'save-review',
    submitReview: 'submit-review',
    confirmInterview: 'confirm-interview',
  },
};

export type PermissionResourceCode =
  (typeof PERMISSION_RESOURCE_CODES)[keyof typeof PERMISSION_RESOURCE_CODES];

export const EMPLOYEE_CENTER_PERMISSION_RESOURCES = [
  {
    name: '我的档案',
    code: PERMISSION_RESOURCE_CODES.selfProfile,
    actions: Object.values(PERMISSION_ACTION_CODES.selfProfile).map((name) => ({
      name,
    })),
  },
  {
    name: '我的证明申请',
    code: PERMISSION_RESOURCE_CODES.selfCertificateRequests,
    actions: Object.values(PERMISSION_ACTION_CODES.selfCertificateRequests).map(
      (name) => ({ name }),
    ),
  },
  {
    name: '我的任职变化',
    code: PERMISSION_RESOURCE_CODES.selfLifecycle,
    actions: Object.values(PERMISSION_ACTION_CODES.selfLifecycle).map(
      (name) => ({ name }),
    ),
  },
  {
    name: '我的离职交接',
    code: PERMISSION_RESOURCE_CODES.selfResignationHandover,
    actions: Object.values(PERMISSION_ACTION_CODES.selfResignationHandover).map(
      (name) => ({ name }),
    ),
  },
];

export const ATTENDANCE_CENTER_PERMISSION_RESOURCES = [
  {
    name: '我的考勤概览',
    code: PERMISSION_RESOURCE_CODES.attendanceMyOverview,
    actions: Object.values(PERMISSION_ACTION_CODES.attendanceMyOverview).map(
      (name) => ({ name }),
    ),
  },
  {
    name: '我的打卡记录',
    code: PERMISSION_RESOURCE_CODES.attendanceMyRecords,
    actions: Object.values(PERMISSION_ACTION_CODES.attendanceMyRecords).map(
      (name) => ({ name }),
    ),
  },
  {
    name: '我的考勤申请',
    code: PERMISSION_RESOURCE_CODES.attendanceMyRequests,
    actions: Object.values(PERMISSION_ACTION_CODES.attendanceMyRequests).map(
      (name) => ({ name }),
    ),
  },
  {
    name: '我的假期',
    code: PERMISSION_RESOURCE_CODES.attendanceMyLeaveBalances,
    actions: Object.values(
      PERMISSION_ACTION_CODES.attendanceMyLeaveBalances,
    ).map((name) => ({ name })),
  },
  {
    name: '我的每月考勤',
    code: PERMISSION_RESOURCE_CODES.attendanceMyMonthly,
    actions: Object.values(PERMISSION_ACTION_CODES.attendanceMyMonthly).map(
      (name) => ({ name }),
    ),
  },
  {
    name: '我的审批',
    code: PERMISSION_RESOURCE_CODES.attendanceApproval,
    actions: Object.values(PERMISSION_ACTION_CODES.attendanceApproval).map(
      (name) => ({ name }),
    ),
  },
];

export const EMPLOYEE_PORTAL_PERMISSION_RESOURCES = [
  ...EMPLOYEE_CENTER_PERMISSION_RESOURCES,
  ...ATTENDANCE_CENTER_PERMISSION_RESOURCES,
  {
    name: '我的工资条',
    code: PERMISSION_RESOURCE_CODES.payrollMy,
    actions: Object.values(PERMISSION_ACTION_CODES.payrollMy).map((name) => ({
      name,
    })),
  },
  {
    name: '我的社保',
    code: PERMISSION_RESOURCE_CODES.socialInsuranceMy,
    actions: Object.values(PERMISSION_ACTION_CODES.socialInsuranceMy).map(
      (name) => ({ name }),
    ),
  },
  {
    name: '我的绩效',
    code: PERMISSION_RESOURCE_CODES.performanceMy,
    actions: Object.values(PERMISSION_ACTION_CODES.performanceMy).map(
      (name) => ({ name }),
    ),
  },
];
